import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { analysisAPI, searchAPI } from "../../lib/api";

const ACCENT = "#2563EB";
const BG = "#06080F";
const SURFACE = "#0A0D13";
const SURFACE_2 = "#0D1117";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

const fitColor = s => s >= 75 ? "#22C55E" : s >= 45 ? "#F59E0B" : "#EF4444";
const Badge = ({ label, color = ACCENT }) => (
  <span style={{ fontSize: 10, fontWeight: 600, fontFamily: "'DM Mono', monospace", color, background: `${color}18`, border: `1px solid ${color}33`, borderRadius: 4, padding: "2px 7px" }}>{label}</span>
);

/* ── Time Ago Helper ── */
const timeAgo = (dateStr) => {
  if (!dateStr) return "N/A";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const GLOBAL_RESULTS_EMPTY = {
  companies: [],
  contacts: [],
  notes: [],
};

const typeIcon = t => ({
  company: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
  contact: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  note: "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
  output: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
}[t] || "");

const typeColor = t => ({ company: ACCENT, contact: "#22C55E", note: "#8B5CF6", output: "#F59E0B" }[t] || TEXT_DIM);

/* ── Global Search Modal (also exported for use from the app shell so
 *   Cmd+K works from any screen, not just Recent Analyses). ── */
export const GlobalSearch = ({ onClose, onViewAnalysis, onNavigate = () => {} }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(GLOBAL_RESULTS_EMPTY);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Debounced live search. Fetches /api/search each time the query
  // stabilises for 200ms. Empty query resets to empty result groups.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 1) { setResults(GLOBAL_RESULTS_EMPTY); setLoading(false); return; }
    setLoading(true);
    const handle = setTimeout(() => {
      searchAPI.search(q)
        .then(r => {
          const d = r?.data || {};
          setResults({
            companies: Array.isArray(d.companies) ? d.companies : [],
            contacts: Array.isArray(d.contacts) ? d.contacts : [],
            notes: Array.isArray(d.notes) ? d.notes : [],
          });
        })
        .catch(() => setResults(GLOBAL_RESULTS_EMPTY))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(handle);
  }, [query]);

  const hasResults = query.length >= 1;
  const totalHits = results.companies.length + results.contacts.length + results.notes.length;

  // Recent searches are read from localStorage so users see their past
  // queries when they open the modal fresh. Consumed on click to re-run
  // the search.
  const recent = (() => {
    try { return JSON.parse(localStorage.getItem("inference_recent_searches") || "[]").slice(0, 5); }
    catch { return []; }
  })();

  const persistRecent = (q) => {
    try {
      const list = JSON.parse(localStorage.getItem("inference_recent_searches") || "[]");
      const next = [q, ...list.filter(x => x !== q)].slice(0, 8);
      localStorage.setItem("inference_recent_searches", JSON.stringify(next));
    } catch { /* private mode */ }
  };

  const handleResultClick = (r) => {
    persistRecent(query);
    if (r.type === "company" && r.analysis_id) {
      if (onViewAnalysis) onViewAnalysis(r.analysis_id);
      else window.location.href = `/app/analysis/${r.analysis_id}`;
      onClose();
    } else if (r.type === "contact" && r.analysis_id) {
      // Contacts live embedded in their analysis; jump there.
      if (onViewAnalysis) onViewAnalysis(r.analysis_id);
      else window.location.href = `/app/analysis/${r.analysis_id}`;
      onClose();
    } else if (r.type === "note") {
      // Notes live on the Decision Notes section — navigate via the host.
      if (onNavigate) onNavigate("notes");
      onClose();
    }
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 300, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: 80, backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div onClick={e => e.stopPropagation()} style={{
        width: "100%", maxWidth: 620,
        background: SURFACE, border: `1px solid ${BORDER}`,
        borderRadius: 14, overflow: "hidden",
        boxShadow: "0 40px 80px rgba(0,0,0,0.6)",
        animation: "fadeUp 0.2s ease",
      }}>
        {/* Search input */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 18px", borderBottom: `1px solid ${BORDER}` }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={TEXT_MUTED} strokeWidth={2}><path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search companies, contacts, notes, outputs…"
            style={{
              flex: 1, background: "none", border: "none", outline: "none",
              fontSize: 15, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif",
            }}
          />
          {query && (
            <button onClick={() => setQuery("")} style={{ background: "none", border: "none", cursor: "pointer", color: TEXT_DIM, padding: 2 }}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          )}
          <span style={{ fontSize: 11, color: TEXT_DIM, background: BORDER, borderRadius: 4, padding: "2px 7px", fontFamily: "'DM Mono', monospace" }}>ESC</span>
        </div>

        {/* Results or recents */}
        <div style={{ maxHeight: 420, overflowY: "auto" }} data-testid="global-search-results">
          {!hasResults ? (
            <div style={{ padding: "16px 18px" }}>
              {recent.length > 0 && (
                <>
                  <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", marginBottom: 10 }}>RECENT SEARCHES</div>
                  {recent.map((r, i) => (
                    <button key={r} onClick={() => setQuery(r)} style={{
                      display: "flex", alignItems: "center", gap: 10, width: "100%",
                      background: "none", border: "none", cursor: "pointer", padding: "9px 0",
                      borderBottom: i < recent.length - 1 ? `1px solid ${BORDER}` : "none",
                    }}>
                      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2}><path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      <span style={{ fontSize: 13, color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>{r}</span>
                    </button>
                  ))}
                </>
              )}
              <div style={{ marginTop: recent.length > 0 ? 20 : 0 }}>
                <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.08em", marginBottom: 10 }}>SEARCH ACROSS</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {["Companies", "Contacts", "Notes"].map(c => (
                    <span key={c} style={{
                      fontSize: 11, color: TEXT_MUTED, background: SURFACE_2,
                      border: `1px solid ${BORDER}`, borderRadius: 5, padding: "3px 10px",
                    }}>{c}</span>
                  ))}
                </div>
              </div>
            </div>
          ) : loading ? (
            <div data-testid="global-search-loading" style={{ padding: "32px 18px", textAlign: "center", fontSize: 13, color: TEXT_MUTED }}>
              Searching…
            </div>
          ) : totalHits === 0 ? (
            <div data-testid="global-search-empty" style={{ padding: "32px 18px", textAlign: "center" }}>
              <p style={{ fontSize: 14, color: TEXT_MUTED }}>No results for &ldquo;<strong style={{ color: TEXT_PRIMARY }}>{query}</strong>&rdquo;</p>
            </div>
          ) : (
            <div>
              {Object.entries(results).map(([section, items]) => {
                if (!items || items.length === 0) return null;
                const typeKey = section.slice(0, -1); // companies → company
                return (
                  <div key={section} data-testid={`global-search-section-${section}`}>
                    <div style={{ padding: "10px 18px 6px", fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                      {section}
                    </div>
                    {items.map((r, i) => (
                      <button key={r.id || i} data-testid={`global-search-hit-${typeKey}-${i}`} onClick={() => handleResultClick({ ...r, type: typeKey })} style={{
                        display: "flex", alignItems: "center", gap: 12, width: "100%",
                        background: "none", border: "none", cursor: "pointer",
                        padding: "10px 18px", transition: "background 0.15s",
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = SURFACE_2}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                      >
                        <div style={{
                          width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                          background: `${typeColor(typeKey)}18`, border: `1px solid ${typeColor(typeKey)}33`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={typeColor(typeKey)} strokeWidth={1.8}><path d={typeIcon(typeKey)} /></svg>
                        </div>
                        <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</div>
                          <div style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.sub}</div>
                        </div>
                        <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke={TEXT_DIM} strokeWidth={2}><path d="M9 18l6-6-6-6" /></svg>
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "10px 18px", borderTop: `1px solid ${BORDER}`, display: "flex", gap: 16 }}>
          {[["↵", "Open"], ["↑↓", "Navigate"], ["ESC", "Close"]].map(([k, l]) => (
            <div key={k} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ fontSize: 10, color: TEXT_DIM, background: BORDER, borderRadius: 3, padding: "1px 5px", fontFamily: "'DM Mono', monospace" }}>{k}</span>
              <span style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Sans', sans-serif" }}>{l}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Analysis Row ── */
const copyAnalysisLink = async (aid) => {
  if (!aid) { toast.error("Cannot copy link — analysis id missing"); return; }
  const url = `${window.location.origin}/app/analysis/${aid}`;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(url);
    } else {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("Link copied to clipboard", { duration: 3000 });
  } catch (e) {
    toast.error("Could not copy to clipboard");
  }
};

const AnalysisRow = ({ a, onViewAnalysis, isSaved, onSave }) => {
  return (
    <div style={{
      background: SURFACE, border: `1px solid ${BORDER}`,
      borderRadius: 10, padding: "14px 18px", marginBottom: 8,
      display: "flex", alignItems: "center", gap: 14,
      transition: "border-color 0.15s",
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = "#2D3748"}
      onMouseLeave={e => e.currentTarget.style.borderColor = BORDER}
    >
      {/* Fit dot */}
      <div data-testid={a.stale ? "fit-dot-stale-list" : undefined}
        title={a.stale ? "Scored against older ICP — re-analyze to update" : undefined}
        style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 8, height: 8, flexShrink: 0 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: fitColor(a.fitScore), boxShadow: `0 0 6px ${fitColor(a.fitScore)}66` }} />
        {a.stale && (
          <span style={{
            position: "absolute", right: -3, bottom: -3,
            width: 7, height: 7, borderRadius: "50%",
            background: "#0B0F19", border: "1px solid #F59E0B",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 4px rgba(245,158,11,0.55)",
          }}>
            <svg width={5} height={5} viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth={3} strokeLinecap="round">
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          </span>
        )}
      </div>

      {/* Name + meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif" }}>{a.name}</span>
          <Badge label={a.source.toUpperCase()} color={a.source === "Batch" ? "#8B5CF6" : TEXT_DIM} />
          {isSaved && <Badge label="SAVED" color="#22C55E" />}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[a.industry, a.region, a.domain, `${a.outputs} outputs`].map((t, i) => (
            <span key={i} style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>{i > 0 ? "· " : ""}{t}</span>
          ))}
        </div>
      </div>

      {/* Modules */}
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", maxWidth: 240, justifyContent: "flex-end" }}>
        {a.modules.slice(0, 3).map(m => (
          <span key={m} style={{ fontSize: 10, color: TEXT_DIM, background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 4, padding: "2px 7px", fontFamily: "'DM Mono', monospace" }}>{m}</span>
        ))}
        {a.modules.length > 3 && <span style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>+{a.modules.length - 3}</span>}
      </div>

      {/* Fit score */}
      <div style={{ textAlign: "center", minWidth: 44 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: fitColor(a.fitScore), fontFamily: "'DM Sans', sans-serif", lineHeight: 1 }}>{a.fitScore}</div>
        <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>FIT</div>
      </div>

      {/* Identity conf */}
      <div style={{ textAlign: "right", minWidth: 60 }}>
        <div style={{ fontSize: 12, color: "#93C5FD", fontFamily: "'DM Mono', monospace" }}>{a.identConf}%</div>
        <div style={{ fontSize: 9, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>IDENTITY</div>
      </div>

      {/* Timestamp */}
      <div style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", minWidth: 60, textAlign: "right" }}>{a.ts}</div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 6 }}>
        <button
          data-testid={`copy-link-${a.id}`}
          onClick={e => { e.stopPropagation(); copyAnalysisLink(a.id); }}
          title="Copy shareable link"
          aria-label="Copy shareable link"
          style={{
            background: "none", border: `1px solid ${BORDER}`,
            borderRadius: 6, padding: "5px 8px", cursor: "pointer",
            color: TEXT_DIM, transition: "color 0.15s, border-color 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "#2D3748"; e.currentTarget.style.color = TEXT_MUTED; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.color = TEXT_DIM; }}
        >
          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </button>
        <button
          data-testid={`save-analysis-${a.id}`}
          onClick={e => { e.stopPropagation(); onSave && onSave(a.id); }}
          style={{
            background: "none", border: `1px solid ${isSaved ? "#22C55E44" : BORDER}`,
            borderRadius: 6, padding: "5px 8px", cursor: "pointer",
            color: isSaved ? "#22C55E" : TEXT_DIM,
          }}
        >
          <svg width={13} height={13} viewBox="0 0 24 24" fill={isSaved ? "#22C55E" : "none"} stroke="currentColor" strokeWidth={2}><path d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" /></svg>
        </button>
        <button data-testid={`open-analysis-${a.id}`} onClick={e => { e.stopPropagation(); if (onViewAnalysis) onViewAnalysis(a.id); }} style={{
          background: `${ACCENT}14`, border: `1px solid ${ACCENT}33`,
          borderRadius: 6, padding: "5px 10px", cursor: "pointer",
          fontSize: 11, color: ACCENT, fontFamily: "'DM Mono', monospace",
        }}>OPEN</button>
      </div>
    </div>
  );
};

/* ── ROOT ── */
export default function InferenceRecentAnalyses({ onViewAnalysis, workspaceId, savedIds, onSaveAnalysis, savedOnly = false, pageTitle, onNavigate }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterSource, setFilterSource] = useState("All");
  const [filterConf, setFilterConf] = useState("All");
  const [filterSaved, setFilterSaved] = useState(savedOnly ? "Saved" : "All");
  const [textSearch, setTextSearch] = useState("");
  const [analyses, setAnalyses] = useState([]);
  const [bulkRefreshing, setBulkRefreshing] = useState(false);

  const handleBulkRefresh = async () => {
    if (!workspaceId || bulkRefreshing) return;
    setBulkRefreshing(true);
    try {
      const res = await analysisAPI.bulkRefresh(workspaceId);
      const { total = 0, refreshed = 0, partial = 0, failed = 0, message } = res.data || {};
      if (total === 0) toast.info("Nothing to refresh", { description: message });
      else if (failed > 0) toast.warning(`Refreshed with ${failed} failure${failed === 1 ? "" : "s"}`, { description: message });
      else if (refreshed === 0 && partial > 0) toast.warning("Partial refresh", { description: message });
      else if (refreshed === 0) toast.info("All briefs already current", { description: message });
      else toast.success(`${refreshed} brief${refreshed === 1 ? "" : "s"} refreshed`, { description: message });
      const list = await analysisAPI.list(workspaceId);
      if (list.data) setAnalyses(prev => prev.map(a => {
        const fresh = list.data.find(x => x.analysis_id === a.id);
        return fresh ? { ...a, fitScore: fresh.fit_score || a.fitScore, stale: !!fresh.icp_stale } : a;
      }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk refresh failed");
    }
    setBulkRefreshing(false);
  };

  useEffect(() => {
    const handler = e => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setSearchOpen(true); } if (e.key === "Escape") setSearchOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!workspaceId) return;
    const fetchAnalyses = async () => {
      try {
        const res = await analysisAPI.list(workspaceId);
        const list = (res.data || []).map((a) => ({
            id: a.analysis_id,
            name: a.company_name || "Unknown",
            domain: a.domain || "N/A",
            industry: a.domain ? a.domain.replace(/\.\w+$/, "").replace(/^www\./, "") : "N/A",
            region: "N/A",
            fitScore: a.fit_score || 0,
            conf: a.fit_score >= 75 ? "High" : a.fit_score >= 45 ? "Medium" : "Low",
            identConf: 85,
            source: a.source || "Manual",
            saved: false,
            stale: !!a.icp_stale,
            ts: timeAgo(a.created_at),
            date: a.created_at ? new Date(a.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "N/A",
            outputs: 4,
            modules: ["Payments", "Signals"],
          }));
        setAnalyses(list);
      } catch (err) {
        console.error("Failed to fetch analyses:", err);
        toast.error("Could not load analyses.");
      }
    };
    fetchAnalyses();
  }, [workspaceId]);

  const filtered = analyses.filter(a => {
    if (filterSource !== "All" && a.source !== filterSource) return false;
    if (filterConf !== "All" && a.conf !== filterConf) return false;
    if (filterSaved === "Saved" && !(savedIds && savedIds.has(a.id))) return false;
    if (textSearch && !a.name.toLowerCase().includes(textSearch.toLowerCase())) return false;
    return true;
  });

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, color: TEXT_PRIMARY, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes fadeUp { from { opacity:0; transform:translateY(-12px); } to { opacity:1; transform:translateY(0); } }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: #1E2433; border-radius: 4px; }
        input::placeholder { color: #374151; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 28px" }}>
        {pageTitle && (
          <div style={{ marginBottom: 20 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.025em", marginBottom: 4 }}>{pageTitle}</h1>
            <p style={{ fontSize: 13, color: TEXT_MUTED }}>
              {savedOnly ? "Companies you've bookmarked for follow-up." : "All analyses run in this workspace."}
            </p>
          </div>
        )}
        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 24 }}>
          {[
            { label: "Total Analyses", value: analyses.length },
            { label: "This Week", value: analyses.filter(a => { const d = a.ts; return d && (d.includes("h ago") || d.includes("m ago")); }).length },
            { label: "Saved", value: savedIds ? analyses.filter(a => savedIds.has(a.id)).length : 0 },
            { label: "Via Batch", value: analyses.filter(a => a.source === "Batch").length },
          ].map((s, i) => (
            <div key={i} style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px 18px" }}>
              <div style={{ fontSize: 10, color: TEXT_DIM, fontFamily: "'DM Mono', monospace", marginBottom: 6, letterSpacing: "0.04em" }}>{s.label.toUpperCase()}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: TEXT_PRIMARY, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.02em" }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
          <input            placeholder="Filter by company name…"
            value={textSearch}
            onChange={e => setTextSearch(e.target.value)}
            style={{
              background: SURFACE_2, border: `1px solid ${BORDER}`, borderRadius: 7,
              padding: "8px 14px", fontSize: 13, color: TEXT_PRIMARY, outline: "none",
              fontFamily: "'DM Sans', sans-serif", width: 220,
            }}
            onFocus={e => e.target.style.borderColor = ACCENT}
            onBlur={e => e.target.style.borderColor = BORDER}
          />
          <div style={{ width: 1, height: 20, background: BORDER }} />
          {/* Source */}
          <div style={{ display: "flex", gap: 5 }}>
            {["All", "Manual", "Batch"].map(f => (
              <button key={f} onClick={() => setFilterSource(f)} style={{
                fontSize: 12, padding: "6px 12px", borderRadius: 6, cursor: "pointer",
                border: `1px solid ${filterSource === f ? ACCENT : BORDER}`,
                background: filterSource === f ? `${ACCENT}18` : "transparent",
                color: filterSource === f ? ACCENT : TEXT_MUTED, fontFamily: "'DM Sans', sans-serif",
              }}>{f}</button>
            ))}
          </div>
          <div style={{ width: 1, height: 20, background: BORDER }} />
          {/* Confidence */}
          <div style={{ display: "flex", gap: 5 }}>
            {["All", "High", "Medium", "Low"].map(f => (
              <button key={f} onClick={() => setFilterConf(f)} style={{
                fontSize: 12, padding: "6px 12px", borderRadius: 6, cursor: "pointer",
                border: `1px solid ${filterConf === f ? ACCENT : BORDER}`,
                background: filterConf === f ? `${ACCENT}18` : "transparent",
                color: filterConf === f ? ACCENT : TEXT_MUTED, fontFamily: "'DM Sans', sans-serif",
              }}>{f}</button>
            ))}
          </div>
          <div style={{ width: 1, height: 20, background: BORDER }} />
          {/* Saved */}
          <div style={{ display: "flex", gap: 5 }}>
            {["All", "Saved"].map(f => (
              <button key={f} onClick={() => setFilterSaved(f)} style={{
                fontSize: 12, padding: "6px 12px", borderRadius: 6, cursor: "pointer",
                border: `1px solid ${filterSaved === f ? ACCENT : BORDER}`,
                background: filterSaved === f ? `${ACCENT}18` : "transparent",
                color: filterSaved === f ? ACCENT : TEXT_MUTED, fontFamily: "'DM Sans', sans-serif",
              }}>{f}</button>
            ))}
          </div>
          <span style={{ marginLeft: "auto", fontSize: 12, color: TEXT_DIM, fontFamily: "'DM Mono', monospace" }}>
            {filtered.length} of {analyses.length} analyses
          </span>
          <button
            data-testid="bulk-refresh-btn"
            onClick={handleBulkRefresh}
            disabled={bulkRefreshing || analyses.length === 0}
            title="Re-map every brief in this workspace with the latest logic — free, no credits used"
            style={{
              display: "flex", alignItems: "center", gap: 6,
              fontSize: 12, padding: "6px 12px", borderRadius: 6,
              cursor: bulkRefreshing ? "wait" : analyses.length === 0 ? "default" : "pointer",
              border: `1px solid ${bulkRefreshing ? ACCENT : BORDER}`,
              background: bulkRefreshing ? `${ACCENT}18` : "transparent",
              color: bulkRefreshing ? ACCENT : TEXT_MUTED,
              fontFamily: "'DM Sans', sans-serif",
              opacity: analyses.length === 0 ? 0.4 : 1,
            }}>
            <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round"
              style={{ animation: bulkRefreshing ? "spin 1s linear infinite" : "none" }}>
              <path d="M21 12a9 9 0 11-6.219-8.56" /><path d="M21 3v6h-6" />
            </svg>
            {bulkRefreshing ? "Refreshing all…" : "Refresh all"}
          </button>
        </div>

        {/* List */}
        {analyses.length === 0 ? (
          <div data-testid="recent-empty-state" style={{ textAlign: "center", padding: "80px 0" }}>
            <p style={{ fontSize: 15, color: TEXT_PRIMARY, marginBottom: 8 }}>No analyses yet.</p>
            <p style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 18 }}>Run your first analysis to get started.</p>
            <button
              data-testid="recent-empty-run-btn"
              onClick={() => { try { window.dispatchEvent(new CustomEvent('inference:nav', { detail: { section: 'analyze' } })); } catch (e) {/* ignore */} const u = new URL(window.location.href); u.searchParams.set('nav','analyze'); window.location.href = u.toString(); }}
              style={{ background: ACCENT, color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
            >Run Analysis</button>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <p style={{ fontSize: 14, color: TEXT_MUTED }}>No analyses match your filters.</p>
          </div>
        ) : (
          filtered.map(a => <AnalysisRow key={a.id} a={a} onViewAnalysis={onViewAnalysis} isSaved={savedIds && savedIds.has(a.id)} onSave={onSaveAnalysis} />)
        )}
      </div>

      {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} onViewAnalysis={onViewAnalysis} onNavigate={onNavigate} />}
    </div>
  );
}
